import { useNavigate, useParams } from 'react-router-dom';
import { useRef } from 'react';
import PageForm from '../../templates/PageForm';
import NewTooltip from '../../atoms/NewTooltip';
import { FaFileExcel } from 'react-icons/fa';
import { exportToExcel } from '../../../services/ExcelExportService';
import useFetch from '../../../hooks/useFetch';
import SicorDocumentCard from '../../organisms/SicorDocumentCard';
import CardList from '../../organisms/CardList';
import APagination from '../../molecules/APagination';
import appConfig from '../../../config/app-config';
import { appendServiceToUrl } from '../../../utils/urlTools';

const BASE_URL = `${appendServiceToUrl(appConfig.apis.sicorApiEnv.address, "/sicor")}/reports`;
const API_KEY = appConfig.apis.sicorApiEnv.key;

interface DocumentReport {
    bacenId: string;
    bank: string;
    emissionDate: string;
    dueDate: string;
    instrumentType: string;
    totalValue: string;
    status: string;
    program: string;
    cityState: string;
    purpose: string;
    rate: string;
    areaHa: string;
    revenue: string;
    productionForecast: string;
    bagsPerHa: string;
    pricePerBag: string;
    irrigation: string;
    ownerDocument: string;
    carNumber: string;
}

const SicorDocumentPageList = () => {
    const navigate = useNavigate();
    const {
        document = '',
        page = '0',
        pageSize = '20',
        field: fieldSorted = 'bank',
        order: orderSorted = 'ASC',
    }: any = useParams();

    const url = `${BASE_URL}/${document}/page-list?page=${page}&pageSize=${pageSize}&order=${fieldSorted},${orderSorted}`;
    const { isLoading, data, error, isTokenError } = useFetch(url, [{ key: 'x-api-key', value: API_KEY }]);

    if (isTokenError) navigate('/denied');
    if (error) navigate('/network-error');

    const exportRows = useRef<DocumentReport[]>([]);

    const handleExport = () => {
        const headers = [
            { id: 'bank', title: 'Banco', field: 'bank', canSort: false },
            { id: 'emissionDate', title: 'Data de emissão', field: 'emissionDate', canSort: false },
            { id: 'dueDate', title: 'Data de vencimento', field: 'dueDate', canSort: false },
            { id: 'instrumentType', title: 'Tipo do instrumento', field: 'instrumentType', canSort: false },
            { id: 'totalValue', title: 'Valor total', field: 'totalValue', canSort: false },
            { id: 'status', title: 'Situação', field: 'status', canSort: false },
            { id: 'program', title: 'Programa', field: 'program', canSort: false },
            { id: 'cityState', title: 'Município/UF', field: 'cityState', canSort: false },
            { id: 'purpose', title: 'Finalidade', field: 'purpose', canSort: false },
            { id: 'rate', title: 'Taxa de juros (%)', field: 'rate', canSort: false },
            { id: 'areaHa', title: 'Área (ha)', field: 'areaHa', canSort: false },
            { id: 'revenue', title: 'Receita bruta (R$)', field: 'revenue', canSort: false },
            { id: 'ownerDocument', title: 'CPF/CNPJ do proprietário', field: 'ownerDocument', canSort: false },
            { id: 'documentFormatted', title: 'CPF/CNPJ', field: 'documentFormatted', canSort: false },
            { id: 'carNumber', title: 'Número do CAR', field: 'carNumber', canSort: false },
        ];
        exportToExcel(headers, exportRows.current, `sicor-${document}.csv`);
    };

    const pageChanged = (p: number) => {
        navigate(`/sicor/document/${document}/${p - 1}/${pageSize}/${fieldSorted}/${orderSorted}`, { replace: true });
    };

    const renderCard = (item: DocumentReport, index: number) => (
        <SicorDocumentCard
            item={item}
            onClick={() => navigate(`/sicor/document/${document}/${item.bacenId}`)}
        />
    );

    const items: DocumentReport[] = data?.list || [];
    exportRows.current = items;

    return (
        <PageForm
            pageTitle={`Detalhes do Documento ${document}`}
            apiVersion="v1"
            isLoading={isLoading}
            breadcrumb={[
                { label: 'Relatórios Sicor', url: '/sicor/report/0/20/document/DESC' },
                { label: document, url: '#' },
            ]}
            controlSlot={
                <div className="flex justify-end">
                    <NewTooltip content="Exportar para Excel" alignment="left">
                        <button
                            type="button"
                            className="text-white bg-blue-500 hover:bg-blue-700 font-medium rounded text-sm px-4 py-2 flex items-center"
                            onClick={handleExport}
                        >
                            <FaFileExcel className="mr-2" />
                            Exportar
                        </button>
                    </NewTooltip>
                </div>
            }
        >
            <div className="space-y-4">
                <CardList
                    items={items}
                    renderCard={renderCard}
                    emptyMessage="Nenhum relatório encontrado..."
                />

                {data && (
                    <APagination
                        currentPage={parseInt(page, 10) + 1}
                        totalPages={data.totalPages || 1}
                        onPageChange={pageChanged}
                    />
                )}
            </div>
        </PageForm>
    );
};

export default SicorDocumentPageList;
